import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PHONES, CONTACT } from '../content';
import { onCallClick, onWhatsappClick } from '../lib/trackHelpers';

const SLIDES = [
  {
    bg: '/images/hero/slide1.webp',
    overlay: 'linear-gradient(110deg,rgba(5,5,13,.97) 0%,rgba(5,5,13,.82) 45%,rgba(5,5,13,.4) 100%)',
    tag: 'Kocaeli Lastik Yol Yardım',
    lines: ['En Yakın', 'Lastikçi.'],
    accent: 'Kocaeli 7/24 Yol Yardım.',
    sub: 'Kocaeli genelinde acil lastik yol yardım. Lastiğiniz patladı mı? Ortalama 30 dakikada yanınızdayız.',
  },
  {
    bg: '/images/hero/slide2.webp',
    overlay: 'linear-gradient(110deg,rgba(5,5,13,.97) 0%,rgba(5,5,13,.82) 45%,rgba(5,5,13,.4) 100%)',
    tag: 'Mobil Lastikçi Kocaeli',
    lines: ['Servise Gitme.'],
    accent: 'Biz Gelelim.',
    sub: 'Mobil lastikçi hizmeti ile bulunduğunuz yerde lastik tamiri ve değişimi. Zaman kaybetmeyin.',
  },
  {
    bg: '/images/hero/slide3.webp',
    overlay: 'linear-gradient(110deg,rgba(5,5,13,.98) 0%,rgba(5,5,13,.85) 45%,rgba(5,5,13,.5) 100%)',
    tag: 'Lastik Tamiri ve Değişimi',
    lines: ['Her Marka,'],
    accent: 'Her Ebat Stokta.',
    sub: 'Goodyear, Lassa, Pirelli, Bridgestone ve daha fazlası — acil lastik değişimi anında.',
  },
  {
    bg: '/images/hero/slide4.webp',
    overlay: 'linear-gradient(110deg,rgba(5,5,13,.97) 0%,rgba(5,5,13,.82) 45%,rgba(5,5,13,.4) 100%)',
    tag: 'Oto Lastik Yol Yardım',
    lines: ['Garantili', 'Tamir.'],
    accent: 'Güvenli Yol.',
    sub: '24 aylık garanti. Kocaeli, İzmit, Gebze, Körfez ve tüm ilçelerde oto lastik yol yardım.',
  },
  {
    bg: '/images/hero/slide5.webp',
    overlay: 'linear-gradient(110deg,rgba(5,5,13,.97) 0%,rgba(5,5,13,.82) 45%,rgba(5,5,13,.4) 100%)',
    tag: 'Lastikçi Ara 7/24',
    lines: ['Cumartesi mi?'],
    accent: 'Fark Etmez.',
    sub: '365 gün, 24 saat. Tatiller, geceler, hafta sonları dahil — Kocaeli acil lastikçi.',
  },
];

const TRUST = [
  { text: 'Ort. 30 dk', color: 'var(--red)' },
  { text: '12+ ilçe', color: 'var(--blue)' },
  { text: '24 ay garanti', color: 'var(--green)' },
  { text: '3 aktif hat', color: '#ff9500' },
];

export default function Hero() {
  const [cur, setCur]       = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setCur(c => (c + 1) % SLIDES.length), []);
  const prev = useCallback(() => setCur(c => (c - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 5800);
    return () => clearInterval(t);
  }, [paused, next]);

  const s = SLIDES[cur];

  return (
    <section
      data-testid="hero-section"
      style={{ position: 'relative', height: '100dvh', minHeight: 640, overflow: 'hidden' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Background */}
      <AnimatePresence mode="sync">
        <motion.div key={`bg-${cur}`}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: [.4, 0, .2, 1] }}
          style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${s.bg})`,
            backgroundSize: 'cover', backgroundPosition: 'center',
          }}
        />
      </AnimatePresence>

      {/* Gradient overlay */}
      <div style={{ position: 'absolute', inset: 0, background: s.overlay, zIndex: 1 }} />

      {/* Headlight glow — warm amber at bottom — creates "night road" feeling */}
      <div style={{
        position: 'absolute', bottom: 0, left: '50%',
        transform: 'translateX(-50%)',
        width: '70%', height: '45%',
        background: 'radial-gradient(ellipse at 50% 100%, rgba(255,170,50,.13) 0%, rgba(255,110,20,.05) 45%, transparent 70%)',
        zIndex: 2, pointerEvents: 'none',
      }} />
      {/* Bottom fade to bg */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '28%', background: 'linear-gradient(to top,rgba(5,5,13,1) 0%,transparent 100%)', zIndex: 2 }} />

      {/* Content */}
      <div className="wrap" style={{
        position: 'relative', zIndex: 3,
        height: '100%', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', paddingTop: 86,
      }}>
        <AnimatePresence mode="wait">
          <motion.div key={`content-${cur}`}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: .5, ease: [.25, .46, .45, .94] }}
            style={{ maxWidth: 640 }}
          >
            {/* Live status + tag */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '1.375rem', flexWrap: 'wrap' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '.4rem',
                background: 'var(--green-dim)', border: '1px solid var(--green-border)',
                borderRadius: 100, padding: '.28rem .875rem',
                fontSize: '.67rem', fontWeight: 700, letterSpacing: '.07em',
                textTransform: 'uppercase', color: 'var(--green)',
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} className="live" />
                Şu An Aktif
              </div>
              <span className="eyebrow" style={{ color: 'rgba(122,132,160,.6)' }}>{s.tag}</span>
            </div>

            {/* Headline */}
            <div style={{ marginBottom: '1.25rem' }}>
              {s.lines.map((line, i) => (
                <div key={`line-${line.substring(0,10)}`} style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
                  fontSize: 'clamp(2.6rem,7.5vw,5.5rem)',
                  lineHeight: .96, letterSpacing: '-.045em',
                  color: 'var(--text)',
                }}>
                  {line}
                </div>
              ))}
              <div style={{
                fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
                fontSize: 'clamp(2.6rem,7.5vw,5.5rem)',
                lineHeight: .96, letterSpacing: '-.045em',
                color: 'var(--blue)',
              }}>
                {s.accent}
              </div>
            </div>

            {/* Sub */}
            <p style={{
              fontSize: 'clamp(.85rem,1.6vw,.975rem)',
              color: 'rgba(122,132,160,.8)', lineHeight: 1.72,
              marginBottom: '2rem', maxWidth: 500,
            }}>
              {s.sub}
            </p>

            {/* CTAs */}
            <div className="hero-cta-wrap" style={{ display: 'flex', flexWrap: 'wrap', gap: '.75rem', alignItems: 'center', marginBottom: '.75rem' }}>
              <a href={PHONES.main_tel}
                data-testid="hero-call-now-button"
                className="btn btn-primary btn-xl ring num"
                onClick={onCallClick('/')}
                style={{ gap: '.6rem', letterSpacing: '.01em' }}>
                <Phone size={17} />
                Hemen Ara — Bekleme
              </a>

              <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
                data-gtm-action="whatsapp_click"
                className="btn btn-green"
                onClick={onWhatsappClick('/')}
                style={{ gap: '.5rem', padding: '.875rem 1.5rem', borderRadius: 'var(--rl)', fontSize: '.9375rem', fontWeight: 700 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M5.077 18.828l.532-1.869A8.85 8.85 0 0 1 3.2 12C3.2 7.134 7.134 3.2 12 3.2c4.866 0 8.8 3.934 8.8 8.8 0 4.866-3.934 8.8-8.8 8.8a8.845 8.845 0 0 1-4.893-1.471l-2.03.499zM12 1C6.477 1 2 5.477 2 11c0 1.821.439 3.538 1.215 5.057L2 22l6.071-1.59A10.976 10.976 0 0 0 12 21c5.523 0 10-4.477 10-10S17.523 1 12 1z"/>
                </svg>
                Konum Gönder
              </a>

              <Link to="/acil-yol-yardim"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '.3rem', color: 'var(--text2)', fontSize: '.875rem', fontWeight: 500, transition: 'color .2s', marginLeft: '.25rem' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--blue)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text2)'}>
                Hizmetler <ArrowRight size={14} />
              </Link>
            </div>

            {/* Urgency microcopy */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '.4rem',
              marginBottom: '1.75rem',
              fontSize: '.75rem', color: 'var(--green)', fontWeight: 600,
            }}>
              <span className="live" style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />
              Şu an müsaitiz — hemen yönleniyoruz
            </div>

            {/* Trust */}
            <div className="hero-trust-badges" style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
              {TRUST.map(({ text, color }) => (
                <div key={text} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '.35rem',
                  background: 'rgba(13,14,32,.75)', backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(28,31,60,.8)',
                  borderRadius: 100, padding: '.28rem .875rem',
                  fontSize: '.75rem', color: 'var(--text2)',
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: color, display: 'inline-block' }} />
                  {text}
                </div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Slide controls */}
        <div style={{
          position: 'absolute', bottom: '2.25rem',
          left: '50%', transform: 'translateX(-50%)',
          zIndex: 4, display: 'flex', alignItems: 'center', gap: '.75rem',
        }}>
          {[['‹', prev], ['›', next]].map(([icon, fn], idx) => idx === 0 ? (
            <button key="prev-btn" onClick={fn} aria-label="Önceki"
              style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(28,31,60,.7)', border: '1px solid var(--border)', color: 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', fontWeight: 700, transition: 'all .18s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--blue)'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'var(--blue)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(28,31,60,.7)'; e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.borderColor = 'var(--border)'; }}>
              {icon}
            </button>
          ) : null)}

          <div style={{ display: 'flex', gap: '.375rem', alignItems: 'center' }}>
            {SLIDES.map((_, i) => (
              <button key={`dot-${i}`} onClick={() => setCur(i)}
                style={{ width: i === cur ? 22 : 6, height: 6, borderRadius: 3, background: i === cur ? 'var(--blue)' : 'rgba(64,69,96,.7)', border: 'none', padding: 0, transition: 'all .3s ease', cursor: 'pointer' }} />
            ))}
          </div>

          {[['‹', prev], ['›', next]].map(([icon, fn], idx) => idx === 1 ? (
            <button key="next-btn" onClick={fn} aria-label="Sonraki"
              style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(28,31,60,.7)', border: '1px solid var(--border)', color: 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', fontWeight: 700, transition: 'all .18s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--blue)'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'var(--blue)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(28,31,60,.7)'; e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.borderColor = 'var(--border)'; }}>
              {icon}
            </button>
          ) : null)}
        </div>
      </div>
    </section>
  );
}
